import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';

import { TranslateService } from '@ngx-translate/core';

import * as authActions from '../actions/authentication.actions';

import { JwtUser } from '@models/authentication/jwt-user.model';

import { AuthenticationService } from '@app/modules/authentication/services/authentication.service';
import { NotificationsService } from '@app/modules/notifications/services/notifications.service';

import { AppRoutes, AuthRoutes } from '@app/shared/routes/routes';

import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

@Injectable()
export class AuthenticationEffects {
  // Login
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.login),
      mergeMap((authAction: any) =>
        this.authService.login(authAction.payload).pipe(
          map((data: any) => {
            const userDetails = this.authService.getUserDetails(data.token);
            this.authService.storeToken(data.token);
            return authActions.loginSuccess({ data: userDetails });
          }),
          catchError((error) => {
            if (error?.error?.IsError){
              this.notificationService.error(
                this.translateService.instant('general.actions.error'),
                this.translateService.instant(`general.customErrors.authentication.${error?.error?.ErrorCode}`)
              );
            }
            return of(authActions.loginFailed({ error }));
          })
        )
      )
    )
  );

  // Refresh Token
  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.refreshToken),
      mergeMap(() =>
        this.authService.refreshToken().pipe(
          map((data: any) => {
            const userDetails = this.authService.getUserDetails(data.token);
            this.authService.storeToken(data.token);
            return authActions.refreshTokenSuccess({ data: userDetails });
          }),
          catchError((error) => of(authActions.refreshTokenFailed({ error })))
        )
      )
    )
  );

  // Logout
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.logout),
      mergeMap(() =>
        this.authService.logout().pipe(
          map(() => {
            this.router.navigate([AuthRoutes.Login.url]);
            return authActions.logoutSuccess();
          }),
          catchError((error) => of(authActions.logoutFailed({ error })))
        )
      )
    )
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.forgotPassword),
      mergeMap((authAction: any) =>
        this.authService.forgotPassword(authAction.email).pipe(
          map(() => {
            this.notificationService.success(
              this.translateService.instant('general.actions.success'),
              this.translateService.instant('general.notifications.authentication.forgotPasswordSuccess')
            );
            return authActions.forgotPasswordSuccess();
          }),
          catchError((error) => {
            if (error?.error?.IsError && error?.error?.ErrorCode === 'MailNotExist'){
              this.notificationService.error(
                this.translateService.instant('general.actions.error'),
                this.translateService.instant('general.customErrors.authentication.mailNotExist')
              );
            } else {
              this.notificationService.error(
                this.translateService.instant('general.actions.error'),
                this.translateService.instant('general.notifications.authentication.forgotPasswordFailed')
              );
            }
            return of(authActions.forgotPasswordFailed({ error }));
          })
        )
      )
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.resetPassword),
      mergeMap((action: any) =>
        this.authService.resetPassword(action.payload).pipe(
          switchMap((data: any) => {
            const userDetails = this.authService.getUserDetails(data.token);
            this.authService.storeToken(data.token);
            this.notificationService.success(
              this.translateService.instant('general.actions.success'),
              this.translateService.instant('general.notifications.authentication.resetPasswordSuccess')
            );
            return [authActions.resetPasswordSuccess(), authActions.loginSuccess({ data: userDetails })];
          }),
          catchError((error) => {
            this.notificationService.error(
              this.translateService.instant('general.actions.error'),
              this.translateService.instant('general.notifications.authentication.resetPasswordFailed')
            );
            return of(authActions.resetPasswordFailed({ error }));
          })
        )
      )
    )
  );

  @Effect({ dispatch: false }) loginSuccess$ = this.actions$.pipe(
    ofType(authActions.loginSuccess),
    map(() => {
      // makes sure authUser is loaded in the store
      const jwtUser: JwtUser = this.authService.getUserDetails();
      if (!jwtUser || !jwtUser.role) {
        return;
      }

      // navigate inside the app when not already in
      if (!this.location.path().includes(AppRoutes.root.url)) {
        this.router.navigate([AppRoutes.root.url]);
      }
    })
  );

  constructor(
    private authService: AuthenticationService,
    private actions$: Actions,
    private notificationService: NotificationsService,
    private translateService: TranslateService,
    private router: Router,
    private location: Location
  ) {}
}
